import { Document, Page, View, Text, Image, StyleSheet, Font } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: { padding: 40, fontFamily: 'Helvetica', fontSize: 11 },
  header: { borderBottomWidth: 2, paddingBottom: 8, marginBottom: 12, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  headerCenter: { borderBottomWidth: 2, paddingBottom: 8, marginBottom: 12, alignItems: 'center' },
  title: { fontSize: 14, fontFamily: 'Helvetica-Bold' },
  subtitle: { fontSize: 8, color: '#777', marginTop: 2 },
  logo: { height: 30, objectFit: 'contain' },
  fieldsGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 12 },
  fieldItem: { width: '50%', flexDirection: 'row', marginBottom: 5 },
  fieldLabel: { fontFamily: 'Helvetica-Bold', fontSize: 9 },
  fieldValue: { fontSize: 9, marginLeft: 3 },
  sectionHeading: { fontSize: 8, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', letterSpacing: 0.5, borderBottomWidth: 0.5, borderBottomColor: '#ccc', paddingBottom: 2, marginTop: 10, marginBottom: 6, color: '#444' },
  groupHeading: { fontSize: 11, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', letterSpacing: 1, borderBottomWidth: 1.5, borderBottomColor: '#333', paddingBottom: 4, marginTop: 18, marginBottom: 8, color: '#111' },
  tickRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2, paddingTop: 3, paddingBottom: 5, borderBottomWidth: 0.5, borderBottomColor: '#f0f0f0' },
  checkbox: { width: 12, height: 12, borderWidth: 1, borderColor: '#333', marginRight: 8, marginTop: 1, flexShrink: 0 },
  tickText: { fontSize: 10, flex: 1 },
  subNote: { fontSize: 8, color: '#777', fontStyle: 'italic', marginTop: 2 },
  note: { fontSize: 9, fontStyle: 'italic', color: '#555', paddingLeft: 20, marginBottom: 3, marginTop: 2 },
  writeIn: { fontSize: 9, paddingLeft: 20, marginBottom: 3, marginTop: 2 },
  writeLine: { borderBottomWidth: 0.5, borderBottomColor: '#888', width: 120, marginLeft: 4 },
  footer: { position: 'absolute', bottom: 25, left: 40, right: 40, borderTopWidth: 0.5, borderTopColor: '#ccc', paddingTop: 5, flexDirection: 'row', justifyContent: 'space-between', fontSize: 7, color: '#999' },
  pageNumber: { fontSize: 7, color: '#999' },
});

const PrintItemPdf = ({ item }) => {
  if (item.type === 'heading') return <Text style={styles.sectionHeading}>{item.text}</Text>;
  if (item.type === 'tick') {
    if (item.sub_note) {
      return (
        <View style={{ marginBottom: 3, borderBottomWidth: 0.5, borderBottomColor: '#e0e0e0', paddingBottom: 4 }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', paddingTop: 3, paddingBottom: 2 }}>
            <View style={styles.checkbox} />
            <Text style={styles.tickText}>{item.text}</Text>
          </View>
          <Text style={{ fontSize: 8, color: '#777', fontStyle: 'italic', paddingLeft: 20, marginBottom: 1 }}>{item.sub_note}</Text>
        </View>
      );
    }
    return (
      <View style={styles.tickRow}>
        <View style={styles.checkbox} />
        <Text style={styles.tickText}>{item.text}</Text>
      </View>
    );
  }
  if (item.type === 'note') return <Text style={styles.note}>{item.text}</Text>;
  if (item.type === 'write_in') return (
    <View style={[styles.writeIn, { flexDirection: 'row', alignItems: 'flex-end' }]}>
      <Text>{item.text}: </Text>
      <View style={styles.writeLine} />
    </View>
  );
  return null;
};

const fieldLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function ChecklistPDF({ printContext, logoBase64 }) {
  const ctx = printContext;
  if (!ctx) return null;

  const fields = ctx.project_fields || {};
  const accentColour = ctx.print_accent_colour || '#1C3557';
  const pos = ctx.logo_position || 'top_left';

  const renderLogo = () => {
    if (!logoBase64) return null;
    return <Image src={logoBase64} style={styles.logo} />;
  };

  const headerStyle = pos === 'top_centre'
    ? [styles.headerCenter, { borderBottomColor: accentColour }]
    : [styles.header, { borderBottomColor: accentColour }];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={headerStyle} fixed>
          {pos === 'top_left' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <View>{renderLogo()}</View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={styles.title}>{ctx.template_title}</Text>
                <Text style={styles.subtitle}>{ctx.template_title} {ctx.template_version}</Text>
              </View>
            </View>
          )}
          {pos === 'top_centre' && (
            <View style={{ alignItems: 'center' }}>
              {renderLogo()}
              <Text style={[styles.title, { marginTop: 4 }]}>Project Start Checklist</Text>
              <Text style={styles.subtitle}>{ctx.template_title} {ctx.template_version}</Text>
            </View>
          )}
          {pos === 'top_right' && (
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <View>
                <Text style={styles.title}>{ctx.template_title}</Text>
                <Text style={styles.subtitle}>{ctx.template_title} {ctx.template_version}</Text>
              </View>
              <View>{renderLogo()}</View>
            </View>
          )}
          {!logoBase64 && pos !== 'top_centre' && pos !== 'top_right' && pos !== 'top_left' && (
            <View>
              <Text style={styles.title}>Project Start Checklist</Text>
              <Text style={styles.subtitle}>{ctx.template_title} {ctx.template_version}</Text>
            </View>
          )}
        </View>

        <View style={styles.fieldsGrid}>
          {Object.entries(fields).map(([key, val]) => (
            <View key={key} style={styles.fieldItem}>
              <Text style={styles.fieldLabel}>{fieldLabel(key)}:</Text>
              <Text style={styles.fieldValue}>{val}</Text>
            </View>
          ))}
          <View style={styles.fieldItem}>
            <Text style={styles.fieldLabel}>Date:</Text>
            <Text style={styles.fieldValue}>___________</Text>
          </View>
        </View>

        {(ctx.sections || []).map((section, si) => (
          <View key={si}>
            {section.section_group ? <Text style={styles.groupHeading}>{section.section_group}</Text> : null}
            {(section.items || []).map((item, ii) => <PrintItemPdf key={ii} item={item} />)}
          </View>
        ))}

        <View style={styles.footer} fixed>
          <Text>Generated by CheckBuild - {fields.project_name}</Text>
          <Text>{ctx.completed_at ? new Date(ctx.completed_at).toLocaleDateString() : ''}</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}
